import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsAPI, permissionTagsAPI } from '../../services/api';
import toast from 'react-hot-toast';

/**
 * 上傳報表 Modal
 */
export default function UploadReportModal({ onClose, onSuccess }) {
  const [name, setName] = useState('');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  // 取得權限標籤列表
  const { data: tagsData } = useQuery({
    queryKey: ['permissionTags'],
    queryFn: permissionTagsAPI.getAll,
  });

  const permissionTags = tagsData?.data || [];

  // 處理檔案拖放
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      if (!name) {
        // 自動填入檔名作為報表名稱
        setName(droppedFile.name.replace('.csv', ''));
      }
    } else {
      toast.error('請上傳 CSV 檔案');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!name) {
        setName(selectedFile.name.replace('.csv', ''));
      }
    }
  };

  // 處理上傳
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      toast.error('請選擇檔案');
      return;
    }
    if (!name.trim()) {
      toast.error('請輸入報表名稱');
      return;
    }
    if (!year) {
      toast.error('請選擇年度');
      return;
    }

    setUploading(true);
    try {
      await reportsAPI.upload(file, name.trim(), year, selectedTags);
      toast.success('報表上傳成功');
      onSuccess();
    } catch (error) {
      toast.error(error.message || '上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  // 切換標籤選擇
  const toggleTag = (tagName) => {
    setSelectedTags(prev =>
      prev.includes(tagName)
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName]
    );
  };

  // 產生年度選項（前後5年）
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">上傳報表</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* 檔案上傳區 */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            } ${file ? 'bg-green-50 border-green-300' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="flex items-center justify-center gap-2 text-green-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">{file.name}</span>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="ml-2 text-gray-500 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-gray-600 mb-2">拖放 CSV 檔案到此處</p>
                <p className="text-gray-400 text-sm mb-2">或</p>
                <label className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer inline-block">
                  選擇檔案
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </>
            )}
          </div>

          {/* 報表名稱 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              報表名稱 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如：2024年度供應商交易報表"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* 年度 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              年度 <span className="text-red-500">*</span>
            </label>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              {years.map(y => (
                <option key={y} value={y}>{y} 年</option>
              ))}
            </select>
          </div>

          {/* 權限標籤 */}
          {permissionTags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                權限標籤
              </label>
              <p className="text-xs text-gray-500 mb-2">
                選擇可存取此報表的權限標籤（未選擇則僅管理員可見）
              </p>
              <div className="flex flex-wrap gap-2">
                {permissionTags.filter(tag => tag.is_active).map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.name)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                      selectedTags.includes(tag.name)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={selectedTags.includes(tag.name) ? { backgroundColor: tag.color } : {}}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
              {selectedTags.length > 0 && (
                <p className="text-xs text-blue-600 mt-2">
                  已選擇 {selectedTags.length} 個標籤
                </p>
              )}
            </div>
          )}

          {/* 提示 */}
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <p className="font-medium mb-1">CSV 檔案格式說明：</p>
            <ul className="list-disc list-inside space-y-1 text-gray-500">
              <li>第一行為欄位標題</li>
              <li>必須包含：vendor_id、name、contact_email、buyer、business_unit 欄位</li>
              <li>檔案大小限制：10MB</li>
            </ul>
            <button
              type="button"
              onClick={() => {
                const bom = '\uFEFF';
                const csv = bom + 'vendor_id,name,contact_email,buyer,business_unit\n';
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'vendor_report_template.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="mt-2 text-blue-600 hover:text-blue-800 underline text-sm font-medium"
            >
              下載 CSV 範本
            </button>
          </div>

          {/* 按鈕 */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              disabled={uploading}
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={uploading || !file}
            >
              {uploading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  上傳中...
                </>
              ) : '上傳'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
